import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { useBrand } from '../brand/BrandProvider';
import type { NetWorthResponse } from '../api/types';
import { workspaceKey } from '../lib/workspaceQueryKey';

/** The picker renders through a floor by design: it makes no workspace-scoped calls,
 *  which is exactly why the step-up wall offers it as an exit (see StepUpProvider).
 *  Holding paint here would break that exit, and because the exit is a full-page
 *  navigation the app would boot straight back into the same floored workspace and
 *  hold again. That is a loop, so the picker is exempt. */
const WORKSPACE_PICKER_PATH = '/select-workspace';

/**
 * Holds the first paint of the workspace shell when the active workspace carries a
 * step-up assurance floor, so the step-up wall is the FIRST thing the user sees after
 * signing in rather than the second.
 *
 * THE PROBLEM. The wall is reactive: nothing knew a floor existed until a
 * workspace-scoped request came back `403 STEP_UP_REQUIRED` with `reason: 'floor'`,
 * and the shell paints optimistically in the meantime (WorkspaceProvider deliberately
 * does not gate on `/me`, and the Dashboard is eager-loaded and fetches on mount). So
 * a floored session entry rendered nav chrome and empty skeletons for one round trip
 * before the wall landed on top. No data leaked, but it showed the user the SHAPE of a
 * workspace they were not yet verified for, which reads as a leak even when it is not.
 *
 * THE SIGNAL. `assuranceFloor` on the membership, forwarded by the façade's `/me` from
 * appcore's `GET /w/{id}` — a route appcore made floor-EXEMPT precisely so a floored
 * SPA can resolve the workspace it is gating (appcore middleware's
 * `FLOOR_EXEMPT_ROUTE_KEYS`). The façade already read that record for the workspace
 * name, so this costs no extra request anywhere.
 *
 * WHAT THIS DOES NOT DO. It never decides whether the session is authorized, or even
 * whether it satisfies the floor: the server knows the workspace is floored, not
 * whether THIS session has stepped up. So the flag decides exactly one thing, whether
 * to hold paint, and the probe's own 403/200 still decides everything else, exactly as
 * before. That keeps F18's rule intact — the client must not reconstruct, from observed
 * HTTP history, a fact the server holds with certainty — and it is why there is no
 * "have I verified this session?" state anywhere in here. Five versions of F18 died on
 * that inference.
 *
 * HOW THE HOLD ENDS. The probe below is a real workspace-scoped read, so the client's
 * interceptor handles it the ordinary way: on a floor 403 it runs the ceremony (the
 * wall, painted over this surface, on the same `bg-cream`, so there is no flash between
 * the two), then retries and resolves with data. The probe succeeding IS the signal
 * that the hold can lift. If the session was already elevated the probe simply 200s and
 * the hold lasts one round trip.
 *
 * FAILING. Every failure mode degrades to the OLD behaviour (paint the shell, let a
 * 403 raise the wall) rather than to a blank screen: an unknown floor (the façade's
 * workspace read failed) reads as unfloored, a probe error renders children, and a
 * paused fetch (offline) renders children so the app's own offline states can show.
 * The wall's own exits do not depend on children being mounted, so they keep working
 * while the hold is in effect.
 *
 * LIMITS, deliberately. One case still shows chrome, bounded and not a leak:
 *
 *   The PICKER shows the nav chrome, because /select-workspace is a child of Layout.
 *   That is the exemption working as intended: the chrome is static and
 *   workspace-agnostic. The property this gate provides is "no shape at session entry",
 *   not "no chrome anywhere while unverified".
 *
 * The IN-APP SWITCH used to be a second limit here and no longer is (steward-app#793).
 * It read: WorkspaceProvider's `resetQueries` runs in an effect, so on the render where
 * `activeMembership` becomes the floored workspace the shared `['net-worth']` entry
 * still holds the PREVIOUS workspace's data and `isPending` is false, flashing one
 * frame of the workspace the user just left. That reset is now a LAYOUT effect, so on
 * the in-app switch paths (all discrete click handlers) the frame is no longer
 * paintable. Do not restore the caveat without first checking which hook that block
 * uses; #852 tracks the async-continuation paths it does not cover, none of which
 * reach this gate.
 */
export function AssuranceFloorGate({ children }: { children: ReactNode }) {
  const token = useAccessToken();
  const { activeMembership, activeWorkspaceId, meLoaded } = useWorkspace();
  const { pathname } = useLocation();

  // Trailing slash tolerated: an exact compare would hold `/select-workspace/`, probe
  // it, and raise the wall over the one page that renders through a floor.
  const onPicker = pathname.replace(/\/+$/, '') === WORKSPACE_PICKER_PATH;
  const exempt = onPicker || !token;

  // Hold while the posture is still UNKNOWN, not just while it is known to be floored.
  // `activeMembership` is `memberships.find(...)`, so it is null until `/me` resolves —
  // and on that render an unheld gate would let the shell paint and the flash straight
  // back in. Today FirstRunGate happens to block on the same `['me']` key first, but it
  // is behind a build flag, so relying on it would make this work by coincidence.
  //
  // This deliberately reverses WorkspaceProvider's "the app shell is NOT gated on /me"
  // stance for floored builds. It is safe because `meLoaded` is `data !== undefined ||
  // isError` with `retry: false`: it settles either way, so it can never dead-end. The
  // cost is one round trip of this surface in a build where FirstRunGate is off; where
  // it is on, `/me` is already cached and this costs nothing.
  const postureUnknown = !exempt && !meLoaded;
  const floored = !exempt && activeMembership?.assuranceFloor === 'stepup';

  // Deliberately the Dashboard's OWN net-worth key and fetcher, not a private one:
  // React Query then serves the Dashboard from this result instead of refetching, so a
  // floored entry costs zero extra requests and the shell paints populated rather than
  // skeletal. If that key is ever renamed the only consequence is one duplicate read,
  // never a wrong hold — the coupling cannot break correctness. `/net-worth` is a
  // single snapshot read that resolves to null when none exists yet, so it is safe to
  // ask for in a workspace with no data at all.
  const probe = useQuery({
    queryKey: workspaceKey('net-worth', activeWorkspaceId),
    queryFn: () => createApiClient(token!).get<NetWorthResponse>('/net-worth'),
    enabled: floored,
    // No retry: a hard failure should hand the app back to its own error handling
    // quickly, not hold a blank screen through several backoff delays. The step-up
    // path is NOT a retry — the interceptor runs the ceremony and re-issues the
    // request itself, so this query sees a single success.
    retry: false,
  });

  // `fetchStatus === 'paused'` means offline: the request will not be attempted at all,
  // so `isPending` would otherwise hold this surface up indefinitely.
  const probing = floored && probe.isPending && probe.fetchStatus !== 'paused';

  if (postureUnknown || probing) return <OpeningWorkspace />;

  return <>{children}</>;
}

/**
 * The quiet surface the wall arrives over. Same `bg-cream` as the wall's session-entry
 * shell so the transition between them is invisible, and deliberately nothing but the
 * brand: no nav, no page frame, nothing that hints at what the workspace contains.
 *
 * `role="status"` because the alternative is silence: a screen-reader user would
 * otherwise hear nothing at all between the sign-in page and the wall's own dialog.
 *
 * The rule pulses. The probe is bounded at 15s by the client's request timeout, and a
 * completely motionless screen reads as frozen long before that.
 */
function OpeningWorkspace() {
  const { shortName, kicker } = useBrand();
  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center bg-cream px-4"
      role="status"
      aria-live="polite"
    >
      {kicker && (
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate font-sans">
          {kicker}
        </div>
      )}
      <div className="font-display text-[21px] font-semibold uppercase tracking-[0.16em] text-navy">
        {shortName}
      </div>
      <div className="mt-2 h-px w-8 animate-pulse bg-bronze" />
      <p className="mt-5 m-0 text-[12px] text-slate font-sans">Opening your workspace…</p>
    </div>
  );
}
